import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { Select } from "antd";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import MultiSelect from "../../../../components/MultiSelect";
import { Option } from "../../../../interfaces/MultiSelectTypes";
import { getServices } from "../../../../apis/service";
import { getVisitServices, addVisitServices, updateVisitServices } from "../../../../apis/visit";
import { getClinicUsers } from "../../../../apis/user";
import { useNotification } from "../../../../NotificationContext";
import { getPackages, getPackageById } from "../../../../apis/packages";

interface ServiceModalProps {
    isOpen: boolean;
    onClose: () => void;
    onServiceAdded: (services: any[]) => void;
    visitId?: number;
}

type ServiceDateOverride =
    | { type: "single"; date: string }
    | { type: "range"; from: string; to: string };

interface ServiceItem {
    id?: number;
    serviceId: number;
    name: string;
    fee: number;
    quantity: number;
    amount?: number;
    clinicUserId?: number | null;
    packageId?: number;
    isAssigneeRequired?: boolean;
    serviceDateOverride?: null | ServiceDateOverride;
    startTime?: string | null;
    endTime?: string | null;
    /** From package_services: used to compute payout_amount (not shown in UI) */
    payoutMode?: "percentage" | "fixed" | null;
    payoutValue?: number | null;
    payoutCap?: number | null;
    /** Set when loading from API; sent back on update if no payout config */
    payoutAmount?: number | null;
}

interface ClinicUser {
    id: number;
    clinicUserId?: number;
    firstName: string;
    lastName: string;
    phone?: string;
}

const ServiceModal: React.FC<ServiceModalProps> = ({
    isOpen,
    onClose,
    onServiceAdded,
    visitId,
}) => {
    const { showNotification } = useNotification();
    // const [isOpenDropdown, setIsOpenDropdown] = useState(false);
    const [isPackageDropdownOpen, setIsPackageDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [serviceSelectValue, setServiceSelectValue] = useState<number | null>(null);
    const [services, setServices] = useState<any[]>([]);
    const [selectedServices, setSelectedServices] = useState<ServiceItem[]>([]);
    const [clinicUsers, setClinicUsers] = useState<ClinicUser[]>([]);
    const [openDropdownServiceId, setOpenDropdownServiceId] = useState<number | null>(null);
    const [existingServices, setExistingServices] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [clinicUserSearchTerm, setClinicUserSearchTerm] = useState("");
    const [packages, setPackages] = useState<any[]>([]);
    const [selectedPackageId, setSelectedPackageId] = useState<number | "">("");
    const [selectedPackageIds, setSelectedPackageIds] = useState<number[]>([]);
    const [commonServiceDate, setCommonServiceDate] = useState<string>(() =>
        format(new Date(), "yyyy-MM-dd")
    );
    const [dateOverrideServiceId, setDateOverrideServiceId] = useState<number | null>(null);
    const [dateOverrideMode, setDateOverrideMode] = useState<"single" | "range" | null>(null);

    useEffect(() => {
        if (isOpen) {
            fetchServices();
            fetchClinicUsers();
            fetchPackages();
            if (visitId) {
                fetchExistingServices();
            } else {
                setSelectedServices([]);
                setExistingServices([]);
            }
        }
    }, [isOpen, visitId]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (!target.closest('.clinic-user-dropdown-container')) {
                setOpenDropdownServiceId(null);
                setClinicUserSearchTerm("");
            }
            if (!target.closest('.date-override-popover-container')) {
                setDateOverrideServiceId(null);
                setDateOverrideMode(null);
            }
        };

        if (openDropdownServiceId !== null || dateOverrideServiceId !== null) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [openDropdownServiceId, dateOverrideServiceId]);


    const fetchPackages = async () => {
        try {
            const res: any = await getPackages();
            console.log(res.data);
            setPackages(res.data || []);
        } catch (err) {
            console.log(err);
            setPackages([]);
        }
    };

    const fetchServices = async (search: string = "") => {
        try {
            const {
                data: { data },
            } = await getServices({ name: search });
            setServices(data);
        } catch (err) {
            console.log(err);
        }
    };

    /** Payout config now comes from service master (and optionally user override when assignee is set). */
    const getPackageServicesList = (pkg: any): { serviceId: number; name: string; fee: number; quantity: number; isAssigneeRequired: boolean; payoutMode: "percentage" | "fixed"; payoutValue: number; payoutCap: number }[] => {
        const list = pkg?.packageServices ?? [];
        return list.map((ps: any) => {
            const s = ps.service ?? ps;
            const sid = s?.id ?? ps.serviceId;
            const fee = Number(ps.fee ?? s?.fee ?? 0) || 0;
            const qty = Math.max(1, Number(ps.quantity ?? 1) || 1);
            const assigneeRequired = s?.assignee_required ?? s?.assigneeRequired ?? false;
            const mode = s?.payout_mode ?? s?.payoutMode ?? "fixed";
            const payoutMode = mode === "percentage" ? "percentage" : "fixed";
            const payoutValue = Number(s?.payout_value ?? s?.payoutValue ?? 0) || 0;
            const payoutCap = Number(s?.payout_cap ?? s?.payoutCap ?? 0) || 0;
            return {
                serviceId: Number(sid),
                name: s?.name ?? "",
                fee,
                quantity: qty,
                isAssigneeRequired: !!assigneeRequired,
                payoutMode,
                payoutValue,
                payoutCap,
            };
        }).filter((x: any) => x.serviceId && x.name);
    };

    const handlePackageSelect = async (option: Option) => {
        const id = Number(option.value);
        if (selectedPackageIds.includes(id)) return;
        let pkg = packages.find((p) => p.id === id);
        if (!pkg?.packageServices?.length) {
            try {
                const res: any = await getPackageById(id);
                pkg = res?.data ?? res;
            } catch (err) {
                console.log(err);
                showNotification("error", "Failed to load package details", "", "topRight");
                return;
            }
        }
        const serviceList = getPackageServicesList(pkg);
        if (serviceList.length === 0) {
            showNotification("info", "This package has no services", "", "topRight");
            setSelectedPackageIds([...selectedPackageIds, id]);
            return;
        }
        const newItems: ServiceItem[] = serviceList.map((x) => ({
            serviceId: x.serviceId,
            name: x.name,
            fee: x.fee,
            quantity: x.quantity,
            amount: x.fee * x.quantity,
            clinicUserId: null,
            packageId: id,
            isAssigneeRequired: x.isAssigneeRequired,
            serviceDateOverride: undefined,
            payoutMode: x.payoutMode,
            payoutValue: x.payoutValue,
            payoutCap: x.payoutCap,
        }));
        setSelectedServices((prev) => {
            const existingIds = new Set(prev.map((s) => s.serviceId));
            const toAdd = newItems.filter((item) => !existingIds.has(item.serviceId));
            toAdd.forEach((item) => existingIds.add(item.serviceId));
            return [...prev, ...toAdd];
        });
        setSelectedPackageIds([...selectedPackageIds, id]);
    };

    const handlePackageRemove = (option: Option) => {
        const id = Number(option.value);
        setSelectedPackageIds(selectedPackageIds.filter((pid) => pid !== id));
        setSelectedServices((prev) => prev.filter((s) => s.packageId !== id));
    };

    const fetchClinicUsers = async () => {
        try {
            const response: any = await getClinicUsers();
            setClinicUsers(response.data || []);
        } catch (err) {
            console.log(err);
        }
    };

    const fetchExistingServices = async () => {
        if (!visitId) return;
        try {
            setIsLoading(true);
            const response: any = await getVisitServices(visitId);
            const servicesData = response.data || [];
            setExistingServices(servicesData);

            const transformedServices: ServiceItem[] = servicesData
                .map((item: any) => {
                    const serviceId = item.serviceId ?? item.service?.id;
                    if (!serviceId) return null;
                    const fee = item.fee ?? 0;
                    const quantity = item.quantity ?? 1;
                    const sd = item.serviceDate ?? item.service_date;
                    const sdFrom = item.serviceDateFrom ?? item.service_date_from;
                    const sdTo = item.serviceDateTo ?? item.service_date_to;
                    let serviceDateOverride: ServiceItem["serviceDateOverride"] = null;
                    if (sdFrom != null && sdTo != null) {
                        serviceDateOverride = { type: "range", from: sdFrom, to: sdTo };
                    } else if (sd != null) {
                        serviceDateOverride = { type: "single", date: sd };
                    }
                    return {
                        id: item.id,
                        serviceId: Number(serviceId),
                        name: item.service?.name ?? item.name ?? "",
                        fee: Number(fee) || 0,
                        quantity: Math.max(1, Number(quantity) || 1),
                        amount: Number(fee) || 0,
                        clinicUserId: item.clinicUserId ?? item.clinic_user_id ?? item.service?.clinicUserId ?? null,
                        serviceDateOverride,
                        startTime: item.startTime ?? item.start_time ?? null,
                        endTime: item.endTime ?? item.end_time ?? null,
                        payoutAmount: item.payoutAmount ?? item.payout_amount ?? null,
                    } as ServiceItem;
                })
                .filter(Boolean) as ServiceItem[];

            setSelectedServices(transformedServices);
            const firstDate = servicesData[0]?.serviceDate ?? servicesData[0]?.service_date;
            if (firstDate) setCommonServiceDate(firstDate);
        } catch (err) {
            console.log(err);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = (search: string) => {
        setSearchTerm(search);
        fetchServices(search);
    };

    const handleServiceSelect = (value: number) => {
        if (selectedServices.some((s) => s.serviceId === value)) return;
        const service = services.find((s) => (s.id ?? s.serviceId) === value);
        if (!service) return;
        const serviceId = service.id ?? service.serviceId;
        if (!serviceId) return;
        const assigneeRequired = !!(service.assignee_required ?? service.assigneeRequired);
        const mode = (service.payout_mode ?? service.payoutMode ?? "fixed") === "percentage" ? "percentage" : "fixed";
        const payoutValue = Number(service.payout_value ?? service.payoutValue ?? 0) || 0;
        const payoutCap = Number(service.payout_cap ?? service.payoutCap ?? 0) || 0;
        setSelectedServices((prev) => [
            ...prev,
            {
                serviceId: Number(serviceId),
                name: service.name,
                fee: service.fee || 0,
                quantity: 1,
                amount: service.fee || 0,
                clinicUserId: null,
                serviceDateOverride: undefined,
                isAssigneeRequired: assigneeRequired,
                payoutMode: mode,
                payoutValue,
                payoutCap,
            },
        ]);
        setServiceSelectValue(null);
    };

    const handleServiceRowRemove = (serviceId: number) => {
        setSelectedServices((prev) => prev.filter((s) => s.serviceId !== serviceId));
        if (openDropdownServiceId === serviceId) {
            setOpenDropdownServiceId(null);
        }
    };

    const handleQuantityChange = (serviceId: number, quantity: number) => {
        setSelectedServices((prev) =>
            prev.map((s) => {
                if (s.serviceId === serviceId) {
                    const qty = Math.max(1, quantity || 1);
                    return { ...s, quantity: qty };
                }
                return s;
            })
        );
    };

    const handleAmountChange = (serviceId: number, amount: number) => {
        setSelectedServices((prev) =>
            prev.map((s) => {
                if (s.serviceId === serviceId) {
                    const amt = Math.max(0, amount || 0);
                    return { ...s, fee: amt, amount: amt };
                }
                return s;
            })
        );
    };

    const handleClinicUserSelect = (serviceId: number, userId: number) => {
        setSelectedServices((prev) =>
            prev.map((s) => {
                if (s.serviceId === serviceId) {
                    return { ...s, clinicUserId: userId };
                }
                return s;
            })
        );
        setOpenDropdownServiceId(null);
        setClinicUserSearchTerm("");
    };

    const handleClinicUserRemove = (serviceId: number) => {
        setSelectedServices((prev) =>
            prev.map((s) => {
                if (s.serviceId === serviceId) {
                    return { ...s, clinicUserId: null };
                }
                return s;
            })
        );
    };

    /** Compute payout_amount from package_services rules: percentage of (fee*quantity) with optional cap, or fixed per unit. */
    const computePayoutAmount = (s: ServiceItem): number | null => {
        const total = (s.fee ?? 0) * (s.quantity ?? 1);
        if (s.payoutMode === "percentage" && (s.payoutValue ?? 0) > 0) {
            let amount = total * (Number(s.payoutValue) / 100);
            const cap = Number(s.payoutCap ?? 0);
            if (cap > 0) amount = Math.min(amount, cap);
            return Math.round(amount * 100) / 100;
        }
        if (s.payoutMode === "fixed" && (s.payoutValue ?? 0) > 0) {
            const amount = Number(s.payoutValue) * (s.quantity ?? 1);
            return Math.round(amount * 100) / 100;
        }
        if (s.payoutAmount != null && s.payoutAmount > 0) return s.payoutAmount;
        return null;
    };

    const getEffectiveServiceDate = (service: ServiceItem) => {
        const ov = service.serviceDateOverride;
        if (!ov) return { display: commonServiceDate, single: commonServiceDate, from: commonServiceDate, to: commonServiceDate };
        if (ov.type === "single") return { display: ov.date, single: ov.date, from: ov.date, to: ov.date };
        return { display: `${ov.from} – ${ov.to}`, single: ov.from, from: ov.from, to: ov.to };
    };

    const setServiceDateOverride = (serviceId: number, override: ServiceItem["serviceDateOverride"], closePopover = true) => {
        setSelectedServices((prev) =>
            prev.map((s) => (s.serviceId === serviceId ? { ...s, serviceDateOverride: override } : s))
        );
        if (closePopover) {
            setDateOverrideServiceId(null);
            setDateOverrideMode(null);
        }
    };

    const openDateOverride = (serviceId: number) => {
        setDateOverrideServiceId(serviceId);
        setDateOverrideMode(null);
    };

    const getFilteredClinicUsers = () => {
        if (!clinicUserSearchTerm.trim()) {
            return clinicUsers;
        }
        const searchLower = clinicUserSearchTerm.toLowerCase();
        return clinicUsers.filter(
            (user) =>
                user.firstName.toLowerCase().includes(searchLower) ||
                user.lastName.toLowerCase().includes(searchLower) ||
                (user.phone && user.phone.includes(searchLower))
        );
    };

    const handleSave = async () => {
        if (!visitId) {
            showNotification(
                "error",
                "Visit ID required",
                "Please ensure visit is loaded before saving services",
                "topRight"
            );
            return;
        }

        // Allow empty services array when updating (to remove all services)
        if (selectedServices.length === 0 && existingServices.length === 0) {
            showNotification(
                "error",
                "No services selected",
                "Please select at least one service",
                "topRight"
            );
            return;
        }

        try {
            setIsLoading(true);
            const invalid = selectedServices.find((s) => !s.serviceId);
            if (invalid) {
                showNotification(
                    "error",
                    "Invalid service selection",
                    "Service ID is missing for one or more services. Please re-select the services.",
                    "topRight"
                );
                return;
            }

            // Prepare payload: backend updates existing visit_service by (visit_id + service_id) or creates new
            const payload = {
                services: selectedServices.map((s) => {
                    const effective = getEffectiveServiceDate(s);
                    const payoutAmount = computePayoutAmount(s);
                    const out: Record<string, unknown> = {
                        serviceId: s.serviceId,
                        fee: s.fee ?? 0,
                        quantity: s.quantity ?? 1,
                        clinicUserId: s.clinicUserId ?? null,
                        serviceDate: effective.single ?? null,
                        startTime: s.startTime ?? null,
                        endTime: s.endTime ?? null,
                        payoutAmount: payoutAmount ?? null,
                    };
                    if (s.serviceDateOverride?.type === "range") {
                        out.serviceDateFrom = effective.from;
                        out.serviceDateTo = effective.to;
                    }
                    return out;
                }),
            };

            // Always use update endpoint when visitId exists
            // Backend will delete all existing services and add only the services in the payload
            const response: any = await updateVisitServices(visitId, payload);
            const savedServices = response.data || [];
            onServiceAdded(savedServices);
            // Update existing services state to reflect the new state from backend
            setExistingServices(savedServices);
            // Update selected services to match what's saved (in case backend modified anything)
            const transformedServices: ServiceItem[] = savedServices
                .map((item: any) => {
                    const serviceId = item.serviceId ?? item.service?.id;
                    if (!serviceId) return null;
                    const fee = item.fee ?? 0;
                    const quantity = item.quantity ?? 1;
                    const sd = item.serviceDate ?? item.service_date;
                    const sdFrom = item.serviceDateFrom ?? item.service_date_from;
                    const sdTo = item.serviceDateTo ?? item.service_date_to;
                    let serviceDateOverride: ServiceItem["serviceDateOverride"] = null;
                    if (sdFrom != null && sdTo != null) {
                        serviceDateOverride = { type: "range", from: sdFrom, to: sdTo };
                    } else if (sd != null) {
                        serviceDateOverride = { type: "single", date: sd };
                    }
                    return {
                        id: item.id,
                        serviceId: Number(serviceId),
                        name: item.service?.name ?? item.name ?? "",
                        fee: Number(fee) || 0,
                        quantity: Math.max(1, Number(quantity) || 1),
                        amount: Number(fee) || 0,
                        clinicUserId: item.clinicUserId ?? item.service?.clinicUserId ?? null,
                        serviceDateOverride,
                        startTime: item.startTime ?? item.start_time ?? null,
                        endTime: item.endTime ?? item.end_time ?? null,
                        payoutAmount: item.payoutAmount ?? item.payout_amount ?? null,
                    } as ServiceItem;
                })
                .filter(Boolean) as ServiceItem[];
            setSelectedServices(transformedServices);
            onClose();
        } catch (err: any) {
            console.log(err);
            showNotification(
                "error",
                "Error saving services",
                err.response?.data?.message || "Failed to save services",
                "topRight"
            );
        } finally {
            setIsLoading(false);
        }
    };

    const getTotal = () =>
        selectedServices.reduce((acc, s) => acc + (s.fee || 0) * (s.quantity || 1), 0);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-xl shadow-lg w-full max-w-6xl h-[86vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b font-semibold text-lg bg-primary text-white flex justify-between items-center">
                    <span>Add Services</span>
                    <button
                        onClick={onClose}
                        className="text-white hover:text-gray-200 text-xl font-bold"
                    >
                        &times;
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-hidden flex flex-col px-6 py-4 mt-4 gap-4">
                    <div className="mb-4 grid grid-cols-2 gap-4 relative z-50 overflow-visible">
                        <div className="flex flex-col gap-2">
                        <label className="block text-sm font-medium text-gray-700 ">
                            Select Package
                        </label>
                        <div>
                            <MultiSelect
                                isOpen={isPackageDropdownOpen}
                                toggleDropdown={() => setIsPackageDropdownOpen(!isPackageDropdownOpen)}
                                searchTerm={searchTerm}
                                onSearch={setSearchTerm}
                                options={packages.map((p) => ({ label: p.name, value: p.id.toString() }))}
                                selectedOptions={selectedPackageIds.map((id) => {
                                    const pkg = packages.find((p) => p.id === id);
                                    return { label: pkg?.name || "", value: id.toString() };
                                })}
                                onOptionSelect={handlePackageSelect}
                                onOptionRemove={handlePackageRemove}
                                onAddNewOption={() => { }}
                                placeholder="Select packages..."
                            />
                        </div>
                        </div>
                        {/* Add Service - single select with search */}
                        <div className="flex flex-col gap-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Add Service
                            </label>
                            <Select
                            className="h-10"
                                style={{ width: "100%", maxWidth: 400 }}
                                placeholder="Search and select a service to add"
                                value={serviceSelectValue ?? undefined}
                                onChange={(value) => {
                                    handleServiceSelect(Number(value));
                                }}
                                onSearch={(v) => handleSearch(v)}
                                showSearch
                                optionFilterProp="label"
                                filterOption={false}
                                options={services
                                    .filter((s) => !selectedServices.some((sel) => sel.serviceId === (s.id ?? s.serviceId)))
                                    .map((s) => ({
                                        value: s.id ?? s.serviceId,
                                        label: s.name,
                                    }))}
                                allowClear
                            />
                        </div>
                    </div>



                    {/* Services and Charges Block */}
                    <div className="flex-1 overflow-hidden flex flex-col mt-0">
                        <div className="bg-white rounded-lg border border-gray-300 flex flex-col h-full">
                            <div className="flex bg-[#ECEFFF] px-4 py-2 flex-row justify-between items-center">
                                <label className="w-full text-base font-medium border-b block">
                                    Services & Charges ({selectedServices.length})
                                </label>
                                <div className="flex items-center gap-3 shrink-0">
                                    <span className="text-sm font-medium text-gray-700">Service date (common):</span>
                                    <input
                                        type="date"
                                        value={commonServiceDate}
                                        onChange={(e) => setCommonServiceDate(e.target.value)}
                                        className="rounded border border-gray-300 px-2 py-1.5 text-sm"
                                    />
                                </div>
                                <div className="w-24 flex justify-end">
                                    <button
                                        onClick={() => setSelectedServices([])}
                                        className="text-red-500 hover:text-red-700 font-bold text-sm"
                                    >
                                        Remove all
                                    </button>
                                </div>
                            </div>
                            <div className="flex-1 overflow-y-auto">
                                <table className="w-full">
                                    <thead className="bg-gray-50 sticky top-0 z-10">
                                        <tr>
                                            <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase min-w-[200px]">
                                                Description
                                            </th>
                                            <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase min-w-[140px]">
                                                Date
                                            </th>
                                            <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                Quantity
                                            </th>
                                            <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                Amount
                                            </th>
                                            <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                                                Total
                                            </th>
                                            <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase w-12"></th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {selectedServices.length > 0 ? (
                                            selectedServices.map((service, index) => {
                                                const effective = getEffectiveServiceDate(service);
                                                const isOverrideOpen = dateOverrideServiceId === service.serviceId;
                                                return (
                                                <tr key={service.serviceId || index}>
                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                                        <div className="flex items-start gap-2 flex-wrap">
                                                            <span className="min-w-0 break-words">{service.name}</span>
                                                            <div className="relative inline-block clinic-user-dropdown-container flex-shrink-0">
                                                                {service.clinicUserId ? (
                                                                    <div className="flex items-center gap-1">
                                                                        <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">
                                                                            <span>
                                                                                {clinicUsers.find(u => u.id === service.clinicUserId)?.firstName} {clinicUsers.find(u => u.id === service.clinicUserId)?.lastName}
                                                                            </span>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleClinicUserRemove(service.serviceId)}
                                                                                className="text-blue-700 hover:text-blue-900 font-bold ml-1"
                                                                            >
                                                                                &times;
                                                                            </button>
                                                                        </div>
                                                                        {service.isAssigneeRequired && <a

                                                                            href="#"
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                setClinicUserSearchTerm("");
                                                                                setOpenDropdownServiceId(service.serviceId);
                                                                            }}
                                                                            className="text-xs text-blue-600 hover:text-blue-800 underline cursor-pointer"
                                                                        >
                                                                            Add User
                                                                        </a>}
                                                                        {openDropdownServiceId === service.serviceId && (
                                                                            <div className="absolute left-0 top-full mt-1 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-[100] max-h-48 flex flex-col overflow-hidden">
                                                                                <div className="px-3 py-2 text-xs font-medium text-gray-600 bg-gray-50 border-b">
                                                                                    Select clinic user
                                                                                </div>
                                                                                <input
                                                                                    type="text"
                                                                                    value={clinicUserSearchTerm}
                                                                                    onChange={(e) => setClinicUserSearchTerm(e.target.value)}
                                                                                    placeholder="Search users..."
                                                                                    className="w-full px-3 py-2 text-sm border-b border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                />
                                                                                <div className="overflow-y-auto overflow-x-hidden max-h-32">
                                                                                    {getFilteredClinicUsers().length > 0 ? (
                                                                                        getFilteredClinicUsers().map((user) => (
                                                                                            <button
                                                                                                key={user.id}
                                                                                                type="button"
                                                                                                onClick={() => {
                                                                                                    handleClinicUserSelect(service.serviceId, user.id);
                                                                                                    setClinicUserSearchTerm("");
                                                                                                }}
                                                                                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 break-words"
                                                                                            >
                                                                                                {user.firstName} {user.lastName}
                                                                                            </button>
                                                                                        ))
                                                                                    ) : (
                                                                                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                                                                                            No users found
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    <>
                                                                        { service.isAssigneeRequired && openDropdownServiceId !== service.serviceId && (
                                                                            <a
                                                                                href="#"
                                                                                onClick={(e) => {
                                                                                    e.preventDefault();
                                                                                    setClinicUserSearchTerm("");
                                                                                    setOpenDropdownServiceId(service.serviceId);
                                                                                }}
                                                                                className="text-xs text-blue-600 hover:text-blue-800 underline cursor-pointer"
                                                                            >
                                                                                Add User
                                                                            </a>
                                                                        )}
                                                                        {openDropdownServiceId === service.serviceId && (
                                                                            <div className="absolute left-0 top-full mt-1 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-[100] max-h-48 flex flex-col overflow-hidden">
                                                                                <div className="px-3 py-2 text-xs font-medium text-gray-600 bg-gray-50 border-b">
                                                                                    Select clinic user
                                                                                </div>
                                                                                <input
                                                                                    type="text"
                                                                                    value={clinicUserSearchTerm}
                                                                                    onChange={(e) => setClinicUserSearchTerm(e.target.value)}
                                                                                    placeholder="Search users..."
                                                                                    className="w-full px-3 py-2 text-sm border-b border-gray-300 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                />
                                                                                <div className="overflow-y-auto overflow-x-hidden max-h-32">
                                                                                    {getFilteredClinicUsers().length > 0 ? (
                                                                                        getFilteredClinicUsers().map((user) => (
                                                                                            <button
                                                                                                key={user.id}
                                                                                                type="button"
                                                                                                onClick={() => {
                                                                                                    handleClinicUserSelect(service.serviceId, user.id);
                                                                                                    setClinicUserSearchTerm("");
                                                                                                }}
                                                                                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 break-words"
                                                                                            >
                                                                                                {user.firstName} {user.lastName}
                                                                                            </button>
                                                                                        ))
                                                                                    ) : (
                                                                                        <div className="px-3 py-2 text-sm text-gray-500 text-center">
                                                                                            No users found
                                                                                        </div>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900 align-top">
                                                        <div className="flex items-center gap-1 date-override-popover-container">
                                                            <span className="text-gray-700">{effective.display}</span>
                                                            <div className="relative inline-block">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => openDateOverride(service.serviceId)}
                                                                    className="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                                                                    title="Override date"
                                                                >
                                                                    <PencilSquareIcon className="h-4 w-4" />
                                                                </button>
                                                                {isOverrideOpen && (
                                                                    <div className="absolute left-0 top-full mt-1 z-[100] min-w-[200px] rounded-md border border-gray-200 bg-white shadow-lg py-2">
                                                                        {dateOverrideMode === null ? (
                                                                            <>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        setServiceDateOverride(service.serviceId, { type: "single", date: effective.single }, false);
                                                                                        setDateOverrideMode("single");
                                                                                    }}
                                                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                                                                >
                                                                                    Single date
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => {
                                                                                        setServiceDateOverride(service.serviceId, { type: "range", from: effective.from, to: effective.to }, false);
                                                                                        setDateOverrideMode("range");
                                                                                    }}
                                                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                                                                >
                                                                                    Date range
                                                                                </button>
                                                                                {service.serviceDateOverride && (
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => setServiceDateOverride(service.serviceId, null)}
                                                                                        className="w-full text-left px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 border-t"
                                                                                    >
                                                                                        Clear override
                                                                                    </button>
                                                                                )}
                                                                            </>
                                                                        ) : dateOverrideMode === "single" ? (
                                                                            <div className="px-3 py-2 space-y-2">
                                                                                <label className="block text-xs font-medium text-gray-600">Single date</label>
                                                                                <input
                                                                                    type="date"
                                                                                    value={service.serviceDateOverride?.type === "single" ? service.serviceDateOverride.date : effective.single}
                                                                                    onChange={(e) => setServiceDateOverride(service.serviceId, { type: "single", date: e.target.value })}
                                                                                    className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                                                                                />
                                                                                <button type="button" onClick={() => setDateOverrideMode(null)} className="text-xs text-gray-500 hover:underline">Back</button>
                                                                            </div>
                                                                        ) : (
                                                                            <div className="px-3 py-2 space-y-2">
                                                                                <label className="block text-xs font-medium text-gray-600">Date range</label>
                                                                                <input
                                                                                    type="date"
                                                                                    value={service.serviceDateOverride?.type === "range" ? service.serviceDateOverride.from : effective.from}
                                                                                    onChange={(e) => {
                                                                                        const from = e.target.value;
                                                                                        const to = service.serviceDateOverride?.type === "range" ? service.serviceDateOverride.to : effective.to;
                                                                                        setServiceDateOverride(service.serviceId, { type: "range", from, to: from > to ? from : to });
                                                                                    }}
                                                                                    className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                                                                                />
                                                                                <input
                                                                                    type="date"
                                                                                    value={service.serviceDateOverride?.type === "range" ? service.serviceDateOverride.to : effective.to}
                                                                                    onChange={(e) => {
                                                                                        const to = e.target.value;
                                                                                        const from = service.serviceDateOverride?.type === "range" ? service.serviceDateOverride.from : effective.from;
                                                                                        setServiceDateOverride(service.serviceId, { type: "range", from, to });
                                                                                    }}
                                                                                    className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                                                                                />
                                                                                <button type="button" onClick={() => setDateOverrideMode(null)} className="text-xs text-gray-500 hover:underline">Back</button>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900">
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            value={service.quantity}
                                                            onChange={(e) => {
                                                                const qty = parseInt(e.target.value) || 1;
                                                                handleQuantityChange(service.serviceId, qty);
                                                            }}
                                                            className="w-20 text-center border rounded px-2 py-1"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900">
                                                        <input
                                                            type="number"
                                                            min={0}
                                                            step="0.01"
                                                            value={service.fee}
                                                            onChange={(e) => {
                                                                const amt = parseFloat(e.target.value) || 0;
                                                                handleAmountChange(service.serviceId, amt);
                                                            }}
                                                            className="w-24 text-center border rounded px-2 py-1"
                                                        />
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900">
                                                        ₹{((service.fee || 0) * (service.quantity || 1)).toFixed(2)}
                                                    </td>
                                                    <td className="px-4 py-3 text-sm text-gray-900">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleServiceRowRemove(service.serviceId)}
                                                            className="text-gray-500 hover:text-red-600 font-bold text-lg"
                                                            aria-label="Remove service"
                                                            title="Remove service"
                                                        >
                                                            &times;
                                                        </button>
                                                    </td>
                                                </tr>
                                                );
                                            })
                                        ) : (
                                            <tr>
                                                <td colSpan={6} className="px-4 py-4 text-sm text-center text-gray-500">
                                                    No services added
                                                </td>
                                            </tr>
                                        )}
                                        <tr className="font-semibold bg-gray-50 sticky bottom-0">
                                            <td className="px-4 py-3 text-sm text-gray-900">Total</td>
                                            <td className="px-4 py-3"></td>
                                            <td className="px-4 py-3"></td>
                                            <td className="px-4 py-3"></td>
                                            <td className="px-4 py-3 text-sm text-gray-900">
                                                ₹{getTotal().toFixed(2)}
                                            </td>
                                            <td className="px-4 py-3"></td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-2 px-6 py-4 border-t">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm rounded-md border"
                        disabled={isLoading}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isLoading || (selectedServices.length === 0 && existingServices.length === 0)}
                        className="px-4 py-2 text-sm text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ServiceModal;
